import { createFileRoute, useParams, useNavigate, useSearch } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog'
import { Label } from '../../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { mdiMagnify, mdiPlus } from '@mdi/js'
import { AppIcon } from '../../components/ui/icon'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useToast } from '../../hooks/use-toast'
import { apiClient } from '../../api/client'
import type { components } from '../../api/schema'
import { DocumentTypesTree, documentTypeLabel } from '../../components/DocumentTypesTree'
import { DocumentTypeEditor } from '../../components/DocumentTypeEditor'
import { z } from 'zod'
import { Can, useCan } from '../../contexts/PermissionsContext'
import { PageHeader } from '../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'

const documentTypesSearchSchema = z.object({
  document_type_uid: z.string().optional(),
  q: z.string().optional(),
})

export const Route = createFileRoute('/$org/document-types')({
  component: DocumentTypesPage,
  validateSearch: documentTypesSearchSchema,
})

type DocumentType = components['schemas']['DocumentType']

function DocumentTypesPage() {
  const { org } = useParams({ from: '/$org/document-types' })
  const navigate = useNavigate({ from: '/$org/document-types' })
  const search = useSearch({ from: '/$org/document-types' })
  const { t } = useTranslation('types')
  const { toast } = useToast()
  const selectedDocTypeUid = search.document_type_uid || null
  const filterQuery = search.q || ''
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newDocType, setNewDocType] = useState({ name: { en: '', fr: '' }, description: { en: '', fr: '' }, code: '', parentUid: '' })
  // The document-type catalogue is configuration: creating, renaming,
  // re-parenting or deleting a type all need `configuration.write`. Without it
  // the tree and the editor stay readable — only the write callbacks are
  // withheld (spec 2026-09-04-02, "Gating").
  const canWriteConfiguration = useCan('configuration.write')

  const queryClient = useQueryClient()

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: 'Home', path: `/${org}` },
    { title: 'Org Settings', path: `/${org}/settings` },
    { title: 'Document Types' }
  ])

  // Fetch document types using the existing query pattern
  const { data: documentTypes = [], isLoading, error } = useQuery({
    queryKey: ['documentTypes', org],
    queryFn: async (): Promise<DocumentType[]> => {
      const response = await apiClient.GET('/api/v1/organizations/{orgCode}/document-types', {
        params: {
          path: { orgCode: org },
          query: { limit: 1000 }
        }
      })

      if (response.error) {
        throw new Error(JSON.stringify(response.error) || 'Failed to load document types')
      }

      return (response.data as { data?: DocumentType[] })?.data || []
    },
  })

  // Get selected document type
  const selectedDocType = documentTypes.find(dt => dt.uid === selectedDocTypeUid) || null

  // Quick filter (#1394): matches the resolved tree label or the code,
  // case-insensitively. A node whose parent doesn't survive the filter is
  // surfaced at root by HierarchyTree rather than dropped, so no ancestor
  // chain needs to be reconstructed here.
  const normalizedFilterQuery = filterQuery.trim().toLowerCase()
  const filteredDocumentTypes = useMemo(() => {
    if (!normalizedFilterQuery) return documentTypes
    return documentTypes.filter(
      dt =>
        documentTypeLabel(dt).toLowerCase().includes(normalizedFilterQuery) ||
        dt.code.toLowerCase().includes(normalizedFilterQuery)
    )
  }, [documentTypes, normalizedFilterQuery])

  // Create document type mutation
  const createDocumentTypeMutation = useMutation({
    mutationFn: async (data: components['schemas']['CreateDocumentTypeRequest']) => {
      const response = await apiClient.POST('/api/v1/organizations/{orgCode}/document-types', {
        params: { path: { orgCode: org } },
        body: data
      })

      if (response.error) {
        throw new Error(JSON.stringify(response.error) || 'Failed to create document type')
      }

      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documentTypes', org] })
      setIsCreateDialogOpen(false)
      setNewDocType({ name: { en: '', fr: '' }, description: { en: '', fr: '' }, code: '', parentUid: '' })
      if (data?.uid) {
        navigate({
          search: (prev) => ({ ...prev, document_type_uid: data.uid })
        })
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to create document type: ' + error.message,
        variant: 'destructive'
      })
    }
  })

  // Update document type mutation
  const updateDocumentTypeMutation = useMutation({
    mutationFn: async ({ uid, data }: { uid: string; data: components['schemas']['UpdateDocumentTypeRequest'] }) => {
      const response = await apiClient.PATCH('/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}', {
        params: {
          path: { orgCode: org, documentTypeUid: uid }
        },
        body: data
      })

      if (response.error) {
        throw new Error(JSON.stringify(response.error) || 'Failed to update document type')
      }

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentTypes', org] })
    }
  })

  // Delete document type mutation
  const deleteDocumentTypeMutation = useMutation({
    mutationFn: async (uid: string) => {
      const response = await apiClient.DELETE('/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}', {
        params: {
          path: { orgCode: org, documentTypeUid: uid }
        }
      })

      if (response.error) {
        throw new Error(JSON.stringify(response.error) || 'Failed to delete document type')
      }

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentTypes', org] })
      navigate({
        search: (prev) => ({ ...prev, document_type_uid: undefined })
      })
    }
  })

  const handleCreate = () => {
    const data: components['schemas']['CreateDocumentTypeRequest'] = {
      name: newDocType.name,
      description: newDocType.description,
      code: newDocType.code,
      parent_document_type_uid: newDocType.parentUid || null
    }
    createDocumentTypeMutation.mutate(data)
  }

  const handleUpdate = async (data: { name?: Record<string, string>; description?: Record<string, string>; parent_document_type_uid?: string | null; flags?: string[] }) => {
    if (!selectedDocTypeUid) return
    await updateDocumentTypeMutation.mutateAsync({
      uid: selectedDocTypeUid,
      data: data as components['schemas']['UpdateDocumentTypeRequest']
    })
  }

  const handleDelete = async () => {
    if (!selectedDocTypeUid) return
    await deleteDocumentTypeMutation.mutateAsync(selectedDocTypeUid)
  }

  const getDisplayName = (docType: DocumentType) => {
    if (typeof docType.name === 'object' && docType.name !== null) {
      return docType.name.en || docType.name.fr || Object.values(docType.name)[0] || docType.code
    }
    return docType.name || docType.code
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-destructive">Error loading document types</p>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['documentTypes', org] })} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="border-b p-4">
        <PageHeader
          icon={SECTION_ICONS.documentTypes}
          title="Document Types"
          description="Manage document types and their associated properties"
          actions={
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <Can perm="configuration.write">
              <DialogTrigger asChild>
                <Button data-testid="create-document-type-button">
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  New Document Type
                </Button>
              </DialogTrigger>
            </Can>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Document Type</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name-en">🇺🇸 Name (English)</Label>
                    <Input
                      id="name-en"
                      placeholder="Contract"
                      value={newDocType.name.en}
                      onChange={(e) => setNewDocType(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                      data-testid="create-name-en"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name-fr">🇫🇷 Name (French)</Label>
                    <Input
                      id="name-fr"
                      placeholder="Contrat"
                      value={newDocType.name.fr}
                      onChange={(e) => setNewDocType(prev => ({ ...prev, name: { ...prev.name, fr: e.target.value } }))}
                      data-testid="create-name-fr"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="description-en">🇺🇸 Description (English)</Label>
                    <Input
                      id="description-en"
                      placeholder="What this document is"
                      value={newDocType.description.en}
                      onChange={(e) => setNewDocType(prev => ({ ...prev, description: { ...prev.description, en: e.target.value } }))}
                      data-testid="create-description-en"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description-fr">🇫🇷 Description (French)</Label>
                    <Input
                      id="description-fr"
                      placeholder="Ce qu'est ce document"
                      value={newDocType.description.fr}
                      onChange={(e) => setNewDocType(prev => ({ ...prev, description: { ...prev.description, fr: e.target.value } }))}
                      data-testid="create-description-fr"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    placeholder="contract"
                    value={newDocType.code}
                    onChange={(e) => setNewDocType(prev => ({ ...prev, code: e.target.value }))}
                    data-testid="create-code"
                  />
                </div>

                <div>
                  <Label htmlFor="parent">Parent Document Type</Label>
                  <Select
                    value={newDocType.parentUid || 'none'}
                    onValueChange={(value) => setNewDocType(prev => ({ ...prev, parentUid: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="create-parent">
                      <SelectValue placeholder="Select parent (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No parent (root level)</SelectItem>
                      {documentTypes.map(docType => (
                        <SelectItem key={docType.uid} value={docType.uid}>
                          {getDisplayName(docType)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                    disabled={createDocumentTypeMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={createDocumentTypeMutation.isPending || !newDocType.name.en || !newDocType.code}
                    data-testid="create-submit"
                  >
                    {createDocumentTypeMutation.isPending ? 'Creating...' : 'Create'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          }
        />
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Tree */}
        <div className="w-1/3 border-r overflow-y-auto">
          <div className="p-2 border-b">
            <div className="relative">
              <AppIcon path={mdiMagnify} size="xs" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filterQuery}
                onChange={(e) => navigate({ search: (prev) => ({ ...prev, q: e.target.value || undefined }), replace: true })}
                placeholder={t('document_types.tree.search_placeholder')}
                className="pl-8 h-8"
                data-testid="document-types-search"
              />
            </div>
          </div>
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : (
            <DocumentTypesTree
              documentTypes={filteredDocumentTypes}
              selectedUid={selectedDocTypeUid || undefined}
              onSelect={(uid) => {
                navigate({
                  search: (prev) => ({ ...prev, document_type_uid: uid })
                })
              }}
              emptyLabel={
                normalizedFilterQuery
                  ? t('document_types.tree.no_match', { query: filterQuery })
                  : undefined
              }
            />
          )}
        </div>

        {/* Right panel - Editor */}
        <div className="flex-1 overflow-y-auto">
          <DocumentTypeEditor
            org={org}
            documentType={selectedDocType}
            allDocumentTypes={documentTypes}
            onUpdate={canWriteConfiguration ? handleUpdate : undefined}
            onDelete={canWriteConfiguration ? handleDelete : undefined}
          />
        </div>
      </div>
    </div>
  )
}
